import React, { useState } from 'react';
import { Form, Upload, Button, Select, message } from 'antd';
import { UploadOutlined, InboxOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { createDataImport, uploadFile } from './DataExportImportServices';
import { resolveBackendMessage } from '../../Utils/apiMessages';
import { Resource } from '../../Constants/socket';
import { useExportImportProgress } from '../../Contexts/ExportImportProgressContext';
import { IMPORTABLE_RESOURCE_VALUES, RESOURCE_LABEL_MAP } from './constants';
import { useErrorHandler } from '../../Hooks/useErrorHandler';

const { Dragger } = Upload;

const DataImportForm = ({ defaultResources, onSuccess }) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [fileList, setFileList] = useState([]);
    const [form] = Form.useForm();
    const { addTask } = useExportImportProgress();
    const { handleError } = useErrorHandler();

    // Determine if we're importing for a single resource (context-aware)
    const isSingleResourceContext = defaultResources && defaultResources.length === 1;

    // For knowledge base, we need to import both KNOWLEDGE_BASE and KNOWLEDGE_ENTRY
    const isKnowledgeBaseContext = isSingleResourceContext && defaultResources[0] === Resource.KNOWLEDGE_BASE;

    // Determine actual resources to use for import
    const getActualResources = () => {
        if (isKnowledgeBaseContext) {
            return [Resource.KNOWLEDGE_BASE, Resource.KNOWLEDGE_ENTRY];
        }
        return defaultResources?.filter(resource => IMPORTABLE_RESOURCE_VALUES.includes(resource));
    };

    const actualDefaultResources = defaultResources ? getActualResources() : [];

    const handleUpload = async (values) => {
        if (fileList.length === 0) {
            message.error(t('please_select_file'));
            return;
        }

        setLoading(true);
        try {
            // Upload the file first to get file_upload_id
            // uploadFile() creates its own FormData internally, so pass the raw File object
            const uploadResponse = await uploadFile(fileList[0].originFileObj);
            const fileUploadId = uploadResponse.data.id; // Adjust based on actual response structure

            // 2. Create the data import
            const payload = {
                file_upload_id: fileUploadId,
                resources: values.resources || actualDefaultResources
            };

            const response = await createDataImport(payload);

            // Add to progress tracker
            if (response?.data?.id) {
                const fileName = fileList[0].name || 'import.zip';
                addTask({
                    id: response.data.id,
                    type: 'import',
                    name: fileName,
                    status: 'processing',
                    progress: 0,
                    serverCreatedAt: response.server_datetime
                });
            }

            const text = resolveBackendMessage(t, response);
            if (text) message.success(text);
            else message.success(t('import_started_successfully'));

            form.resetFields();
            setFileList([]);
            if (onSuccess) onSuccess();

        } catch (error) {
            handleError(error, form);
        } finally {
            setLoading(false);
        }
    };

    const normFile = (e) => {
        if (Array.isArray(e)) {
            return e;
        }
        return e?.fileList;
    };

    const importableResources = IMPORTABLE_RESOURCE_VALUES.map(resource => ({
        label: t(RESOURCE_LABEL_MAP[resource] || resource),
        value: resource
    }));

    // Helper to get resource label for display
    const getResourceLabel = (resourceValue) => {
        const resource = importableResources.find(r => r.value === resourceValue);
        return resource?.label || resourceValue;
    };

    // Get display name for selected resource(s) in single resource context
    const getContextResourceDisplay = () => {
        if (isKnowledgeBaseContext) {
            return t('knowledge_base');
        }
        return getResourceLabel(defaultResources[0]);
    };

    return (
        <Form
            form={form}
            layout="vertical"
            onFinish={handleUpload}
            initialValues={{ resources: actualDefaultResources }}
        >
            {isSingleResourceContext ? null : (
                // General import: show multiselect
                <Form.Item
                    label={t('select_resources_to_import')}
                    name="resources"
                    rules={[{ required: true, message: t('validation_required') }]}
                >
                    <Select
                        mode="multiple"
                        placeholder={t('select_resources_placeholder', 'Select resources')}
                        options={importableResources}
                        maxTagCount="responsive"
                        showSearch
                        filterOption={(input, option) =>
                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                        }
                    />
                </Form.Item>
            )}

            <Form.Item
                label={t('upload_file')}
                name="file"
                valuePropName="fileList"
                getValueFromEvent={normFile}
                rules={[{ required: true, message: t('validation_required') }]}
            >
                <Dragger
                    maxCount={1}
                    beforeUpload={() => false}
                    fileList={fileList}
                    onChange={({ fileList }) => setFileList(fileList)}
                    accept=".json,.csv,.zip,.tar,.tar.gz,.tgz"
                >
                    <p className="ant-upload-drag-icon">
                        <InboxOutlined />
                    </p>
                    <p className="ant-upload-text">{t('click_or_drag_file_to_upload')}</p>
                    <p className="ant-upload-hint">
                        {t('support_single_upload')}
                    </p>
                </Dragger>
            </Form.Item>

            <Form.Item>
                <Button type="primary" htmlType="submit" loading={loading} icon={<UploadOutlined />} block>
                    {t('start_import')}
                </Button>
            </Form.Item>
        </Form>
    );
};

export default DataImportForm;
